import React, { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ContactsButton from '../../../PublicTableElements/ContactsButton';
import { CompanyService } from '../../../../service/CompanyService';
import EditEmployer from '../../../PopupForms/EditEmployer/';
import DeleteModal from '../../../PopupForms/DeleteDialog/DeleteModal';
import TooltipButton from 'components/common/InfoPopup/TooltipButton';
import { MESSAGE_ICON_SIZE } from 'components/common/MessengerLink';
import EmployeeMessengers from './EmployeeMessengers';
import { ICON } from 'components/common/IconButton';
import Pagination from 'components/common/Layout/Pagination';
import Button from 'components/common/Button';
import { useHttp } from 'hooks/http.hook';
import { EMPLOYEE, EMPLOYEE_ENDPOINTS } from 'endpointsConst';
import { ITEMS_PER_PAGE } from 'constants/settings';
import { getFormattedUserName } from 'helpers/chats';
import { formatPhoneWithPlus, getPhoneHref } from 'helpers/phone';
import { showErrorMessage, showSuccessMessage } from 'helpers/notifications';
import { EMPLOYEE_ROLES } from 'constants/user';

import styles from './styles.module.scss';
import { getOffsetAfterDeletion } from 'utils/utils';

const canUpdate = (profile, employeeId) =>
  profile.employee_company_roles === EMPLOYEE_ROLES.OWNER || profile.id === employeeId;

// TODO refactor it and all components here
const CompanyEmployees = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate()
  const { request, loading } = useHttp();
  const user = useSelector((state) => state.profile.profileData);
  const isOwner = user.employee_company_roles === EMPLOYEE_ROLES.OWNER;

  const [employees, setEmployees] = useState({
    items: [],
    count: 0,
  });

  const [editableEmployee, setEditableEmployee] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const getEmployes = useCallback(() => {
    if (user.company_id) {
      const params = new URLSearchParams(location.search);
      const urlOffset = parseInt(params.get('offset')) || 0;

      request(`${EMPLOYEE}/${user.company_id}`, 'get', {
        params: {
          offset: urlOffset,
          limit: ITEMS_PER_PAGE,
        },
      }).then((result) => {
        setEmployees(result.data);
      });
    }
  }, [request, user.company_id, location.search]);

  const handleEditEmployee = (employee) => {
    setEditableEmployee(employee);
  };

  const handleCloseEditEmployee = () => {
    setEditableEmployee(null);
  };

  const handleDelete = (id, closeCallback) => {
    CompanyService.deleteEmployee(id)
      .then(() => {
        closeCallback();
       const params = new URLSearchParams(location.search);
       const currentOffset = parseInt(params.get('offset')) || 0;
       const newOffset = getOffsetAfterDeletion(
        employees.count,
        currentOffset,
        ITEMS_PER_PAGE,
        employees.items.length
      );
      if (newOffset !== null) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('offset', newOffset.toString());
        navigate({
                ...location,
                search: newOffset,
            });
      }
        getEmployes();
      })
      .catch((e) => {
        console.log(e.response);
      });
  };

  const handleSubmit = (id, values) => {
    let updatedEmployees = [];
    if (id) {
      updatedEmployees = employees.items.map((employee) => {
        if (employee.id !== id) {
          return employee;
        }

        return {
          ...employee,
          ...values,
        };
      });
    } else {
      updatedEmployees = employees.items;
      updatedEmployees.push(values);
    }

    setEmployees({
      items: updatedEmployees,
      count: employees.count,
    });
  };

  const handleUpdateCode = (id) => {
    request(EMPLOYEE_ENDPOINTS.CODE, 'patch', {
      user_id: id,
    })
      .then(() => {
        showSuccessMessage(t('success'));
        const updatedEmployees = [...employees.items];
        const employee = updatedEmployees.find((employee) => id === employee.id);
        employee.isExpiredCode = false;
        setEmployees({
          items: updatedEmployees,
          count: employees.count,
        });
      })
      .catch((e) => {
        showErrorMessage(t('error'));
      });
  };

  useEffect(() => {
    getEmployes();
  }, [getEmployes]);

  const hasPagination = employees.count / ITEMS_PER_PAGE > 1;

  const filteredEmployees = searchQuery.trim()
    ? employees.items.filter((el) => {
        const name = getFormattedUserName(el).toLowerCase();
        const query = searchQuery.trim().toLowerCase();
        return name.includes(query) || (el.email || '').toLowerCase().includes(query) || (el.mobile_phone || '').includes(query);
      })
    : employees.items;

  return (
    <>
      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>
          {t('employee.allEmployees')} ({employees.count})
        </span>
        <div className={styles.toolbarActions}>
          <div className={styles.searchWrapper}>
            <input
              className={styles.searchInput}
              type="text"
              placeholder={t('employee.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <img
              className={styles.searchIcon}
              src={new URL('../../../../img/search.svg', import.meta.url).href}
              alt=""
            />
          </div>
          {isOwner && (
            <button
              className={styles.addButton}
              type="button"
              onClick={() => handleEditEmployee({})}
            >
              + {t('employee.add_employee.add_employee_button')}
            </button>
          )}
        </div>
      </div>
      <table className="profile__table profile__table--employees" cellSpacing="0">
        <thead className="profile__table-header">
          <tr className="profile__table-row">
            <th className={styles.tableHead}>{t('employee.name')}</th>
            <th className={styles.tableHead}>{t('employee.position')}</th>
            <th className={styles.tableHead}>{t('employee.contact')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredEmployees.length > 0 &&
            filteredEmployees.map((el) => (
              <tr className="profile__table-row" key={el.id}>
                <td className="profile__table-data">
                  <div className="profile__employer-name">
                    <div className={styles.imageWrapper}>
                      {el.picture ? (
                        <img
                          className={styles.image}
                          src={`${el.picture.indexOf('blob:') + 1 ? '' : el.picture}`}
                          alt=""
                        />
                      ) : (
                        <span className={styles.noImage} />
                      )}
                    </div>
                    {getFormattedUserName(el)}
                  </div>
                </td>
                <td className="profile__table-data">
                  <div className={styles.positionContainer}>
                    <span className={styles.position}>{t('employee.position')}</span>
                    {el.position}
                  </div>
                </td>
                <td className="profile__table-data">
                  <div className={styles.contactWrapper}>
                    <div className={styles.contactLinks}>
                      {el.mobile_phone && (
                        <div className={styles.contactLinkRow}>
                          <a className={styles.contactLink} href={getPhoneHref(el.mobile_phone)}>
                            {formatPhoneWithPlus(el.mobile_phone)}
                          </a>
                          <button
                            type="button"
                            className={styles.copyButton}
                            onClick={() => navigator.clipboard.writeText(formatPhoneWithPlus(el.mobile_phone))}
                            title={t('employee.copy_phone')}
                          >
                            <img
                              src={new URL('../../../../img/copy.svg', import.meta.url).href}
                              alt=""
                              className={styles.copyIcon}
                            />
                          </button>
                        </div>
                      )}
                      {el.email && (
                        <a className={styles.contactLink} href={`mailto:${el.email}`}>
                          {el.email}
                        </a>
                      )}
                      <EmployeeMessengers
                        messengers={el.messengers}
                        size={MESSAGE_ICON_SIZE.small}
                        className={styles.messengerLinks}
                      />
                    </div>
                    {canUpdate(user, el.id) && (
                      <div className={styles.controlsWrapper}>
                        {el.isExpiredCode && isOwner && (
                          <div className={styles.button}>
                            <TooltipButton
                              hintText={t('employee.add_employee.updateCode')}
                              icon={ICON.update}
                              onClick={() => handleUpdateCode(el.id)}
                              disabled={loading}
                            />
                          </div>
                        )}
                        {isOwner && user.id !== el.id && (
                          <div className={styles.button}>
                            <DeleteModal
                              onDeleteClick={handleDelete}
                              data={{
                                title: t('employee.delete'),
                                id: el.id,
                                name: `${el.firstname} ${el.lastname}`,
                              }}
                            />
                          </div>
                        )}
                        <div className={styles.button}>
                          <TooltipButton
                            hintText={t('employee.edit')}
                            icon={ICON.edit}
                            onClick={() => handleEditEmployee(el)}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={styles.contactWrapperMobile}>
                    <span className={styles.mobileContactsTitle}>{t('employee.contact')}</span>
                    <div className="profile__button-container">
                      <EmployeeMessengers
                        messengers={el.messengers}
                        size={MESSAGE_ICON_SIZE.normal}
                        className={styles.messengers}
                      />
                      <div className={styles.contactRow}>
                        <span className={styles.contactLabel}>{t('employee.add_employee.email')}:</span>
                        <a
                          className={styles.contactValue}
                          href={`mailto:${el.email}`}
                          title={t('employee.add_employee.email')}
                        >
                          {el.email}
                        </a>
                      </div>
                      <div className={styles.contactRow}>
                        <span className={styles.contactLabel}>{t('employee.add_employee.phone')}:</span>
                        <a
                          className={styles.contactValue}
                          href={getPhoneHref(el.mobile_phone)}
                          title={t('employee.add_employee.phone')}
                        >
                          {formatPhoneWithPlus(el.mobile_phone)}
                        </a>
                      </div>
                    </div>
                  </div>
                </td>
                <td className={`profile__table-data ${styles.mobileTableDataActions}`}>
                  {canUpdate(user, el.id) && (
                    <>
                      <div className={styles.controlsWrapper}>
                        {el.isExpiredCode && isOwner && (
                          <div className={styles.button}>
                            <TooltipButton
                              hintText={t('employee.add_employee.updateCode')}
                              icon={ICON.update}
                              onClick={() => handleUpdateCode(el.id)}
                              disabled={loading}
                            />
                          </div>
                        )}
                        {isOwner && user.id !== el.id && (
                          <div className={styles.button}>
                            <DeleteModal
                              onDeleteClick={handleDelete}
                              data={{
                                title: t('employee.delete'),
                                id: el.id,
                                name: `${el.firstname} ${el.lastname}`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <Button
                        className={styles.mobileEditButton}
                        onClick={() => handleEditEmployee(el)}
                        disabled={loading}
                      >
                        {t('employee.edit')}
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <div className={styles.footer}>
        <div>
          {hasPagination && (
            <Pagination itemsOnPage={ITEMS_PER_PAGE} count={employees.count} url="/profile/employees" />
          )}
        </div>
        {editableEmployee && (
          <EditEmployer
            onSubmit={handleSubmit}
            onClose={handleCloseEditEmployee}
            employee={editableEmployee}
            companyId={user.company_id}
          />
        )}
      </div>
    </>
  );
};

export default CompanyEmployees;
